/**
 * Remembers which OAuth provider a user last signed in with, so the sign-in
 * screen can surface a "Last used" hint on return.
 *
 * Recognition beats recall: rather than making a returning user remember
 * whether they used Google or Apple, we show them the one that worked last
 * time. Stored only after a confirmed sign-in, client-side, best-effort
 * (localStorage may be blocked in private browsing).
 */

const LAST_PROVIDER_KEY = 'auth_last_provider'

export type RememberedProvider = 'google' | 'apple'

function isRememberedProvider(value: unknown): value is RememberedProvider {
  return value === 'google' || value === 'apple'
}

/** Persist the provider the user just successfully authenticated with. */
export function rememberLastProvider(provider: string | null | undefined): void {
  if (typeof window === 'undefined') return
  if (!isRememberedProvider(provider)) return

  try {
    localStorage.setItem(LAST_PROVIDER_KEY, provider)
  } catch {
    // Ignore - localStorage may be blocked
  }
}

/** Read the last provider the user signed in with, if any. */
export function getLastProvider(): RememberedProvider | null {
  if (typeof window === 'undefined') return null

  try {
    const raw = localStorage.getItem(LAST_PROVIDER_KEY)
    return isRememberedProvider(raw) ? raw : null
  } catch {
    return null
  }
}
